"use client";

import { useEffect, useRef, useCallback, useState } from "react";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const MAX_POLL_ATTEMPTS = 150; // 15 seconds max wait

/**
 * Reusable Turnstile hook — renders the widget into a container ref
 * and returns the current token + a reset function.
 *
 * Ensures the Turnstile script is loaded (injects it if not already
 * present) so the widget works regardless of the layout script strategy.
 */
export function useTurnstile(options?: { theme?: "light" | "dark" | "auto"; active?: boolean }) {
  // When false, the widget isn't rendered yet (e.g. its container is behind a
  // chooser step). Defaults to true so callers that mount their form up front
  // are unaffected. Flipping to true re-runs the effect once the container mounts.
  const active = options?.active ?? true;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Captured once on mount — the widget renders a single time and theme is
  // static per usage, so a ref keeps the [] effect free of reactive deps.
  const themeRef = useRef<"light" | "dark" | "auto">(options?.theme ?? "light");
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setToken(null);
    setError(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    if (!active || !containerRef.current || !SITE_KEY) return;

    // Ensure the Turnstile script is loaded
    if (
      !document.getElementById("cf-turnstile-script") &&
      !document.querySelector(`script[src*="challenges.cloudflare.com/turnstile"]`)
    ) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = SCRIPT_URL;
      s.async = true;
      document.head.appendChild(s);
    }

    let interval: ReturnType<typeof setInterval> | null = null;
    let attempts = 0;

    const render = () => {
      if (!containerRef.current || widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        theme: themeRef.current,
        size: "flexible",
        callback: (t: string) => {
          setToken(t);
          setError(null);
        },
        "error-callback": (code: string) => {
          setError(code);
          setToken(null);
        },
        "expired-callback": () => {
          setToken(null);
        },
      });
    };

    if (window.turnstile) {
      render();
    } else {
      interval = setInterval(() => {
        attempts++;
        if (window.turnstile) {
          clearInterval(interval!);
          interval = null;
          render();
        } else if (attempts >= MAX_POLL_ATTEMPTS) {
          clearInterval(interval!);
          interval = null;
          setError("Verification unavailable. Please refresh.");
        }
      }, 100);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      // A token is tied to its widget — once the widget is removed (e.g. the
      // caller flips `active` off and back on), the old token must not be
      // submitted against the freshly rendered challenge.
      setToken(null);
      setError(null);
    };
  }, [active]);

  return { containerRef, token, error, reset };
}
